// ---------- filled-dice alphabet ----------
//   0        case vide sans point : du papier. Jamais dans la palette (cf. index.ts).
//   1..6     k points d'encre sur une case vide
//   7..12    case pleine, avec (code - 6) points EN RÉSERVE — le même dé, vu de l'autre côté
//   13       case pleine, sans point
//
// Treize codes, quatorze symboles avec le papier nu.
//
// POURQUOI IL EXISTE, et pas juste un réglage de `des`. Neuf points au rayon 0,115 ne couvrent
// que 9 × pi × 0,115² ≈ 0,37 de la case, alors que `des.encode.ts` trame sur une échelle k/9
// qui va jusqu'à 1. Sa couverture est donc bien PROPORTIONNELLE au nombre de points — le
// commentaire de ce fichier a raison — mais avec un facteur de 0,37, pas de 1 : une encre
// voulue à 1,0 sort à 0,37, aucune case de la feuille n'est plus sombre que ça, et la feuille
// est juste dans ses rapports et écrasée d'un facteur trois dans son amplitude. Mesuré sur une
// illustration : 30 % du contraste voulu. C'est très exactement ce que l'œil appelle « terne ».
//
// Deux leviers étaient nécessaires, et l'un sans l'autre ne donne rien : RELEVER LE PLAFOND de
// couverture pour qu'il existe un noir, et LAISSER L'ÉCHELLE DU TRAMAGE LITTÉRALE au lieu de la
// normaliser sur la face la plus couvrante. Retourner le dé fait le premier, et le rayon fait
// le second : à sqrt(1/13pi) un point couvre exactement un treizième de la case, donc la rampe
// est 1/13, 2/13 … 13/13, du papier au noir plein, linéaire de bout en bout sans un palier à
// écrire à la main. Mesuré, l'amplitude passe de 30 % à 79 %.
//
// C'est un ALPHABET NEUF et non un `des` corrigé, pour une raison qui n'est pas esthétique :
// les grilles publiées disent « code k = encre k/9 », et cette numérotation-là n'a plus cours
// ici (le code 7 est la case pleine à un point, la deuxième plus sombre des treize). Les
// redessiner avec cet alphabet donnerait une rampe non monotone sur ses derniers niveaux. Les
// anciens puzzles gardent donc `des`, les nouveaux ont celui-ci — et le serveur ne sert cet id
// qu'aux clients qui le déclarent (cf. api.ts et backend/src/routes/puzzles.ts), donc une
// version déjà installée ne le voit jamais.
//
// Le dessin seulement : la conversion vers ces codes est dans desPlein.encode.ts, que le jeu
// n'importe jamais (cf. types.ts).

import { inkColor } from '../ink';
import type { Alphabet } from './types';

// positions sur la grille 3x3 :  0 1 2
//                                3 4 5
//                                6 7 8
const FACES: readonly number[][] = [
  [], // 0
  [4], // 1
  [0, 8], // 2
  [0, 4, 8], // 3
  [0, 2, 6, 8], // 4
  [0, 2, 4, 6, 8], // 5
  [0, 2, 3, 5, 6, 8], // 6 — deux colonnes de trois, comme sur un dé
];

/** Premier centre et écart entre deux centres, en fraction de la case — ceux de `des`. */
const FIRST = 0.24;
const PITCH = 0.26;

/**
 * Aire d'un point, en fraction de la case, et le rayon qui en découle.
 *
 * Un treizième, et c'est tout le calcul de cet alphabet : les six faces claires couvrent alors
 * 1/13 … 6/13, les six faces pleines 1 - 6/13 … 1 - 1/13, et la case pleine 13/13. La rampe
 * est régulière par construction, et le plafond est le noir.
 */
const PIP_AREA = 1 / 13;
const R = Math.sqrt(PIP_AREA / Math.PI);

/** Le papier de la charte, pour les points en réserve.
 *
 *  Lu une fois dans la feuille de style plutôt qu'écrit ici : c'est la même valeur que
 *  `paintPaper()` peint sous les symboles, et les deux doivent rester la même. Le repli est la
 *  valeur du crème de la charte, pour qu'un dessin fait avant que la feuille de style soit
 *  chargée soit une image en avance et pas une case blanche. */
let paperCss: string | null = null;
function paper(): string {
  if (paperCss) return paperCss;
  const v = getComputedStyle(document.documentElement).getPropertyValue('--paper').trim();
  if (v) paperCss = v;
  return v || '#fbf6ec';
}

function pips(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  list: readonly number[],
  css: string,
): void {
  const r = Math.min(w, h) * R;
  ctx.fillStyle = css;
  ctx.beginPath();
  for (const i of list) {
    const cx = x + w * (FIRST + PITCH * (i % 3));
    const cy = y + h * (FIRST + PITCH * Math.floor(i / 3));
    ctx.moveTo(cx + r, cy); // sans ce moveTo, chaque arc se relierait au précédent
    ctx.arc(cx, cy, r, 0, 7);
  }
  ctx.fill();
}

export const desPlein: Alphabet = {
  id: 'desPlein',
  sheetMode: 'checker',
  bakesOnTop: true,
  label: 'Dés pleins',
  symbolName: 'dé',
  helpText:
    "Les <b>six faces du dé, deux fois</b> : à points d'encre sur une case vide, et en " +
    '<b>réserve</b> sur une case pleine — comme un vrai dé. Plus la case pleine sans point. ' +
    'Pure densité, comme les Dés, mais la rampe va cette fois du papier au noir plein : un ' +
    'point couvre un treizième de la case, donc les treize codes se suivent de 1/13 en 1/13.',

  aspect: () => 1,

  // La couverture croissante, la plus couvrante en dernier (cf. la note de paletteOrder) : les
  // six faces claires, puis les six faces pleines de la plus ajourée à la plus fermée, puis la
  // case pleine. La numérotation, elle, reste mnémonique — le code 7 est « la case pleine à un
  // point » et non « le septième niveau de gris ».
  paletteOrder: [1, 2, 3, 4, 5, 6, 12, 11, 10, 9, 8, 7, 13],

  codeName: (code) => {
    if (code === 13) return 'case pleine';
    if (code <= 6) return `${code} point${code > 1 ? 's' : ''}`;
    const n = code - 6;
    return `case pleine, ${n} point${n > 1 ? 's' : ''} en réserve`;
  },

  draw(ctx, x, y, w, h, code) {
    if (!code) return;
    if (code <= 6) {
      pips(ctx, x, y, w, h, FACES[code], inkColor());
      return;
    }
    ctx.fillStyle = inkColor();
    ctx.fillRect(x, y, w, h);
    if (code < 13) pips(ctx, x, y, w, h, FACES[code - 6], paper());
  },
};

/**
 * Aire commune à deux disques de rayon r dont les centres sont distants de d.
 *
 * Nécessaire parce qu'une face n'est pas toujours la somme de ses points : à ce rayon, les
 * points de la face à six (deux colonnes de trois, donc à 0,26 d'écart) se recouvrent, alors
 * que ceux des faces de un à cinq — coins et centre, à 0,368 d'écart au plus près — ne se
 * touchent jamais. Sans cette correction, deux des treize paliers seraient annoncés 2,5 points
 * de pourcentage trop sombres.
 */
function lens(r: number, d: number): number {
  if (d >= 2 * r) return 0;
  return 2 * r * r * Math.acos(d / (2 * r)) - (d / 2) * Math.sqrt(4 * r * r - d * d);
}

/** Couverture d'encre d'une face, ses recouvrements déduits. Les recouvrements sont comptés
 *  deux à deux, ce qui est exact ici : trois points ne se recouvrent jamais ensemble, les
 *  extrémités d'une colonne étant à 0,52 l'une de l'autre. */
function faceCover(list: readonly number[]): number {
  let a = list.length * PIP_AREA;
  for (let i = 0; i < list.length; i++)
    for (let j = i + 1; j < list.length; j++) {
      const dx = PITCH * ((list[i] % 3) - (list[j] % 3));
      const dy = PITCH * (Math.floor(list[i] / 3) - Math.floor(list[j] / 3));
      a -= lens(R, Math.hypot(dx, dy));
    }
  return a;
}

/**
 * L'échelle du tramage, INDEXÉE PAR CODE et volontairement pas triée.
 *
 * `ditherLevels` cherche le palier le plus PROCHE de l'encre voulue et rend son indice : en
 * rangeant les couvertures dans l'ordre des codes, cet indice EST le code, et la numérotation
 * peut rester mnémonique sans rien coûter. C'est `paletteOrder` qui remet les symboles dans
 * l'ordre de lecture, ce qui est son travail.
 *
 * Littérale, et c'est le second levier de cet alphabet (cf. l'entête) : une case qui veut 0,8
 * d'encre va chercher une face qui couvre vraiment 0,8. Normaliser sur la face la plus
 * couvrante — ce que fait `des` avec son k/9, et `hachures` en rapportant ses paliers au
 * croisillon — revient à décréter que le symbole le plus sombre représente le noir, et à peindre
 * toute la feuille à l'encre du plafond.
 */
export const DES_PLEIN_STEPS: readonly number[] = [
  0,
  ...[1, 2, 3, 4, 5, 6].map((k) => faceCover(FACES[k])),
  ...[1, 2, 3, 4, 5, 6].map((k) => 1 - faceCover(FACES[k])),
  1,
];
